import type { Messages } from './hu'

export const en: Messages = {
  meta: {
    title: 'Low-voltage systems',
    description:
      'Design, installation and maintenance of fire alarm, access control, intrusion detection, CCTV and intercom systems in Budapest and nationwide. EN54, VdS, Grade 3 certified.',
    ogTitle: 'Fire alarm systems and security technology | Archestor Kft.',
    ogDescription:
      'Professional low-voltage systems: fire alarm, access control, CCTV and intrusion detection designed and delivered to European standards.',
    ogLocale: 'en_US'
  },
  nav: {
    home: 'Home',
    about: 'About',
    services: 'Services',
    references: 'References',
    contact: 'Contact',
    menu: 'Menu',
    languageSelection: 'Language selection',
    accessibilityMode: 'Low vision mode',
    accessibilityOn: 'Low vision mode on',
    accessibilityOff: 'Low vision mode off'
  },
  hero: {
    titleLine1: 'Security',
    titleLine2: 'you can',
    titleLine3: 'count on.',
    subtitle:
      'Design, installation and maintenance of complex low-voltage systems to European standards — one hand, one point of accountability.',
    cta: 'Request a quote',
    imageAlt: 'Low-voltage security systems — fire alarm and access control installation',
    stats: {
      projects: 'Successful projects',
      standards: 'European standards',
      expertise: 'System expertise'
    }
  },
  about: {
    label: 'About us',
    titleLine1: 'Hungarian expertise,',
    titleLine2: 'European',
    titleLine3: 'standards.',
    text1:
      'We are a Hungary-based specialist company focused exclusively on the design, installation and maintenance of low-voltage systems.',
    text2:
      'We build reliable, intelligent and future-proof systems for office buildings, industrial facilities, hospitals, public institutions and residential developments.',
    imageAlt: 'Industrial facility low-voltage systems — fire alarm and CCTV'
  },
  services: {
    label: 'Services',
    titleLine1: 'End-to-end',
    titleLine2: 'system integration.',
    items: [
      {
        title: 'Fire alarm systems',
        desc: 'Addressable or conventional, single- or multi-panel systems to EN54 and VdS standards.'
      },
      {
        title: 'Access control systems',
        desc: 'Card, biometric and mobile access — from turnstiles to licence plate recognition barriers.'
      },
      {
        title: 'Intrusion detection',
        desc: 'Perimeter protection, Grade 3 certified systems, up to 520 zones per control panel.'
      },
      {
        title: 'CCTV surveillance',
        desc: 'HD and 4K IP cameras, intelligent video analytics, remote monitoring.'
      },
      {
        title: 'Gas detection',
        desc: 'Detection of explosive and toxic gases, integrated with fire alarm systems.'
      },
      {
        title: 'Fire suppression control',
        desc: 'Control of gas and foam suppression systems, integrated with fire alarm.'
      },
      {
        title: 'Nurse call systems',
        desc: 'Full installation for hospital, care home and accessible restroom facilities.'
      },
      {
        title: 'Intercom systems',
        desc: 'Modern video IP intercoms, including multi-dwelling configurations.'
      },
      {
        title: 'IT networks',
        desc: 'Structured cabling, active network equipment, server room build-outs.'
      },
      {
        title: 'Software development',
        desc: 'Custom security system development through our extensive partner network.'
      },
      {
        title: 'Digital logging',
        desc: 'Comprehensive digital logging for fire protection and building maintenance tasks.'
      },
      {
        title: 'Remote monitoring',
        desc: 'As a registered installer: fire and intrusion alarm signal transmission with response services.'
      }
    ]
  },
  references: {
    label: 'References',
    titleLine1: 'Proud of',
    titleLine2: 'our work so far.',
    more: '...and many more successful projects.',
    items: [
      {
        tag: 'Office building • Budapest (Budapart) • 2025-2026',
        title:
          'Modern office complex access control, intrusion detection, nurse call and intercom system',
        desc: 'We are proud to have participated as a subcontractor in a major flagship development, installing access control, intrusion detection, nurse call and intercom systems across 21 floors.',
        alt: 'Budapart office building — access control, intrusion detection and intercom'
      },
      {
        tag: 'Industrial • Szikszó • 2023-',
        title: 'Manufacturing plant and logistics building integrated low-voltage systems',
        desc: 'Integrated installation and maintenance of fire alarm, suppression control, gas detection, access control, intrusion detection and CCTV as a subcontractor.',
        alt: 'Szikszó industrial plant — integrated fire alarm, access control and CCTV'
      },
      {
        tag: 'Office building • Budapest (Angyalföld) • 2026',
        title: 'Office building fit-out refurbishment',
        desc: 'Fire alarm system fit-out refurbishment across multiple floors for our partner.',
        alt: 'Angyalföld office fit-out — fire alarm system refurbishment'
      },
      {
        tag: 'Logistics park • Polgár • 2025',
        title: 'Logistics park licence plate recognition access control',
        desc: 'Automated licence plate recognition barrier system with 24/7 logging and emergency intercom.',
        alt: 'Polgár logistics park — licence plate recognition access and intercom'
      },
      {
        tag: 'Office building • Budapest (City Park) • 2025',
        title: '21st-century modern office building access control and intrusion detection',
        desc: 'At our partner\'s invitation, we delivered and commissioned security systems for a large-scale office complex in the heart of Budapest.',
        alt: 'City Park office building — modern access control and intrusion detection'
      },
      {
        tag: 'Condominium • Budapest (Zugló) • 2025',
        title: 'Condominium fire alarm system',
        desc: 'Commissioning of a fire alarm system.',
        alt: 'Zugló condominium — fire alarm system installation'
      },
      {
        tag: 'Office building • Budapest • 2024',
        title: 'Office building reconstruction',
        desc: 'Fire alarm system reconstruction with integration of legacy system components.',
        alt: 'Budapest office reconstruction — fire alarm system upgrade'
      },
      {
        tag: 'Office building • Budapest (Kelenföld) • 2024-2025',
        title: 'Office building fit-out refurbishment',
        desc: 'Fire alarm, access control and intercom fit-out refurbishment across multiple floors as a subcontractor for our partner.',
        alt: 'Kelenföld office fit-out — fire alarm and access control'
      },
      {
        tag: 'Industrial • Debrecen • 2023-2025',
        title: 'Manufacturing complex fire alarm system',
        desc: 'As a subcontractor, we supervised and commissioned fire alarm works for one of the region\'s largest developments — a manufacturing complex campus.',
        alt: 'Debrecen manufacturing complex — fire alarm installation and supervision'
      }
    ]
  },
  contact: {
    label: 'Contact',
    titleLine1: 'Let\'s start',
    titlePrefix: '',
    titleEmphasis: 'working together.',
    intro:
      'Share your vision — our experts will get back to you shortly with a tailored proposal.',
    emailLabel: 'Email',
    companyLabel: 'Company details',
    companyName: 'Archestor Kft.',
    companyAddress: 'Registered office: Salgótarján, Arany János út 17.',
    companyReg: 'Company registration no.: 12-09-012726',
    companyTax: 'EU VAT no.: HU32403237',
    formSubject: 'New quote request - Archestor Kft.',
    namePlaceholder: 'Name *',
    companyPlaceholder: 'Company',
    emailPlaceholder: 'Email address *',
    messagePlaceholder: 'Project description…',
    submit: 'Send',
    success: 'Thank you! We will contact you shortly.',
    captchaRequired: 'Please complete the Captcha before sending!',
    captchaFailed: 'Captcha verification failed.',
    captchaError: 'An error occurred during captcha verification.',
    sendError: 'An error occurred while sending.'
  },
  footer: {
    rights: 'All rights reserved.',
    cookieNotice: 'Privacy notice',
    cookieSettings: 'Cookie settings'
  },
  renameNotice: {
    title: 'Our company name has changed',
    body: 'The new name of Fusion Safety Kft.: Archestor Kft.\nOnly our name has changed. Our company details remain the same, and our team continues the work at the usual high standard.',
    close: 'OK'
  },
  cookies: {
    bannerTitle: 'Cookies and privacy',
    bannerDescription:
      'This website uses cookies for operation, contact form spam protection (Cloudflare Turnstile), and — with your consent — remembering language and comfort settings. You can change your settings at any time.',
    acceptAll: 'I accept',
    rejectAll: 'Reject all',
    contactFormBlocked:
      'The contact form is unavailable due to your cookie settings. To send an email, please accept cookies.'
  },
  cookieNotice: {
    title: 'Privacy notice',
    subtitle: 'Information on cookie-related data processing on archestor.com and its subpages',
    close: 'Close',
    sections: [
      {
        title: '1. What cookies are and why we use them',
        body: [
          'Cookies are small data files sent by the website server to your browser and stored on your device.',
          'On archestor.com, cookies are used to operate the website securely and conveniently, record your cookie choice, protect the contact form against spam, and — with your consent — remember language and comfort settings.'
        ]
      },
      {
        title: '2. Accepting or rejecting cookies',
        body: [
          'On this website you cannot choose individual cookie categories separately: you either accept all cookies or reject them.',
          'If you accept, the website uses storage required for operation, remembering language and comfort settings, and spam protection on the contact form (Cloudflare Turnstile).',
          'If you reject, we do not place cookies and the contact form is unavailable.',
          'This website does not use analytics, advertising or marketing cookies.'
        ]
      },
      {
        title: '3. Managing consent',
        body: [
          'On your first visit, a cookie banner appears where you can accept or reject cookies. If you accept, essential and functional cookies are activated; if you reject, non-essential storage is cleared and the contact form becomes unavailable.',
          'You can change your choice at any time using the “Cookie settings” button in the footer. Previously given consent can be withdrawn by choosing reject.'
        ]
      },
      {
        title: '4. Browser cookie settings',
        body: [
          'You can also control cookies in your browser settings. Blocking or deleting cookies may affect the availability of some website features.'
        ]
      }
    ],
    tableTitle: '5. Details of individual cookies',
    tableHeaders: {
      name: 'Name',
      function: 'Function',
      type: 'Type',
      duration: 'Duration',
      purpose: 'Purpose',
      legalBasis: 'Legal basis'
    },
    rows: [
      {
        name: 'archestor-consent',
        function: 'Stores cookie consent',
        type: 'HTTP cookie',
        duration: '1 year',
        purpose: 'Stores the user’s cookie choice',
        legalBasis: 'Legitimate interest / consent'
      },
      {
        name: 'archestor-locale',
        function: 'Language preference',
        type: 'HTTP cookie',
        duration: '1 year',
        purpose: 'Remembers the selected language',
        legalBasis: 'Consent'
      },
      {
        name: 'archestor-country',
        function: 'Country code storage',
        type: 'HTTP cookie',
        duration: '24 hours',
        purpose: 'Supports country-based language suggestion',
        legalBasis: 'Consent'
      },
      {
        name: 'archestor-a11y',
        function: 'Accessibility mode',
        type: 'Local storage',
        duration: 'Until deleted',
        purpose: 'Remembers low vision mode setting',
        legalBasis: 'Consent'
      },
      {
        name: 'archestor-rename-seen',
        function: 'Company name-change notice',
        type: 'Local storage',
        duration: 'Until deleted',
        purpose: 'Remembers that the visitor closed the Fusion Safety name-change notice',
        legalBasis: 'Legitimate interest'
      },
      {
        name: 'Cloudflare Turnstile',
        function: 'Spam protection',
        type: 'Third party (HTTP cookie)',
        duration: 'As per provider documentation',
        purpose: 'Protects the contact form against bots',
        legalBasis: 'Consent'
      },
      {
        name: 'FormSubmit',
        function: 'Email forwarding',
        type: 'Third party (HTTP cookie)',
        duration: 'As per provider documentation',
        purpose: 'Delivers contact form messages',
        legalBasis: 'Consent'
      }
    ],
    thirdPartyTitle: '6. Third-party cookies and data protection',
    thirdPartyServices: [
      {
        description:
          'We use Cloudflare Turnstile to protect the contact form against spam. Turnstile is only loaded after cookie acceptance and operates under Cloudflare’s own privacy policy.',
        privacyLabel: 'Cloudflare privacy policy:',
        privacyUrl: 'https://www.cloudflare.com/privacypolicy/'
      },
      {
        description:
          'We use FormSubmit (formsubmit.co) to forward contact form messages. Email sending is only possible after cookie acceptance; submitted data is delivered to us through the FormSubmit service.',
        privacyLabel: 'FormSubmit privacy policy:',
        privacyUrl: 'https://formsubmit.co/privacy.pdf',
        openInNewTab: false
      },
      {
        description:
          'Incoming contact emails are delivered to Archestor Kft.’s Microsoft 365 (Outlook) mailbox. The email service is provided by Microsoft under its own privacy policy.',
        privacyLabel: 'Microsoft privacy policy:',
        privacyUrl: 'https://privacy.microsoft.com/'
      }
    ],
    operatorTitle: '7. Website operator',
    operatorBody: [
      'Archestor Kft.',
      'Registered office: Salgótarján, Arany János út 17., Hungary',
      'Company registration no.: 12-09-012726',
      'Email: office@archestor.com'
    ],
    hostingTitle: '8. Hosting provider',
    hostingBody: [
      'Cloudflare, Inc.',
      'Registered office: 101 Townsend Street, San Francisco, CA 94107, USA',
      'Website: https://www.cloudflare.com/',
      'Email: privacy@cloudflare.com',
      'The website is served through Cloudflare\'s edge network. For visitors from the EU, content is delivered from EU data centres on European servers; for visitors outside the EU, from the nearest edge server.'
    ],
    contactTitle: '9. Contact and data subject rights',
    contactBody: [
      'For questions about cookies and data processing, contact us at office@archestor.com.',
      'You may object to processing based on legitimate interest and request deletion of your data.'
    ]
  },
  toast: {
    emailSuccess: 'Email sent successfully! We will be in touch soon!'
  },
  schema: {
    description:
      'Hungary-based specialist in the design, installation and maintenance of low-voltage systems (fire alarm, access control, CCTV, intrusion detection).',
    addressLocality: 'Salgótarján',
    catalogName: 'Security technology services',
    areaServed: [
      { type: 'Country', name: 'Hungary' },
      { type: 'City', name: 'Budapest' }
    ],
    services: [
      {
        name: 'Fire alarm systems',
        description: 'Design and installation of EN54 and VdS compliant fire alarm systems.'
      },
      {
        name: 'Access control systems',
        description: 'Card, biometric and licence plate recognition access control.'
      },
      {
        name: 'CCTV and intrusion detection',
        description: 'HD/4K camera systems and Grade 3 intrusion detection.'
      }
    ]
  }
}